import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { HireBarProvider, ToastProvider, Button, CampaignRulesProvider } from '../components/ui';
import usePositionData from './position/usePositionData';
import PositionHeader, { normalizeTab } from './position/PositionHeader';
import OverviewTab from './position/OverviewTab';
import QuestionBankTab from './position/QuestionBankTab';
import InterviewsTab from './position/InterviewsTab';
import InterviewPanel from './position/InterviewPanel';
import NewInterviewDialog from './position/NewInterviewDialog';
import CandidatesTab from './position/CandidatesTab';
import { ByInterviewView, ByBatchView } from './position/ResultViews';
import { BatchRows, BatchesTab, BatchAddCandidatesDialog, groupEntriesByBatch } from './position/BatchUI';
import { CATALOG_TYPES } from '../config/assessmentFamilies';
import './position/position.css';

/** In-app "leave without saving?" prompt for the question bank. */
function UnsavedChangesDialog({ onStay, onLeave }) {
  return (
    <>
      <div className="wz-scrim" onClick={onStay} />
      <div className="wz-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) onStay(); }}>
        <div className="wz-dialog-box" role="alertdialog" aria-modal="true" aria-label="Unsaved changes" style={{ width: 'min(420px, 100%)' }}>
          <div className="wz-dialog-head">
            <h2 style={{ fontSize: 18 }}>Unsaved changes</h2>
            <Button iconOnly icon="x" aria-label="Close" onClick={onStay} />
          </div>
          <p className="wz-sub" style={{ padding: '20px 28px', lineHeight: 1.5 }}>
            You have unsaved question changes. If you leave now, they will be lost.
          </p>
          <div className="wz-dialog-foot" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={onStay} autoFocus>Stay</Button>
            <Button variant="danger" onClick={onLeave}>Leave without saving</Button>
          </div>
        </div>
      </div>
    </>
  );
}

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const onUnauthorized = useCallback(() => navigate('/admin/login'), [navigate]);
  const data = usePositionData(id, { onUnauthorized });
  const { campaign, loading } = data;
  const activeTab = normalizeTab(searchParams.get('tab'));
  const qbDirty = useRef(false);
  const onQbDirtyChange = useCallback((dirty) => { qbDirty.current = dirty; }, []);
  const [pendingLeave, setPendingLeave] = useState(null); // () => void, run if the user confirms
  const guardLeave = useCallback((go) => {
    if (activeTab === 'questions' && qbDirty.current) setPendingLeave(() => go);
    else go();
  }, [activeTab]);
  const setActiveTab = useCallback((tab) => {
    if (tab === activeTab) return;
    guardLeave(() => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next);
    });
  }, [activeTab, guardLeave, searchParams, setSearchParams]);

  // Cross-tab navigation. Panel/expansion state lives here, not in the URL, so
  // closing a panel never navigates.
  const [ivSel, setIvSel] = useState(null);
  const [candOpen, setCandOpen] = useState(null);
  const [qset, setQset] = useState('jd');
  const [newOpen, setNewOpen] = useState(false);
  // Interview type whose setup sent the recruiter to the Question bank; saving questions brings them back.
  const [ivReturn, setIvReturn] = useState(null);
  const [candView, setCandView] = useState('candidate'); // candidate | interview | batch
  const [candIv, setCandIv] = useState('all');
  // Add candidates dialog: { esId, mode: 'new' | 'existing', bid } (mode/bid only matter with batches on).
  const [addFor, setAddFor] = useState(null);
  const [ivOpen, setIvOpen] = useState(() => new Set()); // interviews expanded to show batches
  const [batchIv, setBatchIv] = useState(null); // interview picked in Candidates > Batch view
  const [collapsed, setCollapsed] = useState(() => new Set()); // collapsed sections in interview/batch views
  const openAdd = useCallback((esId, mode, bid) => {
    const hasBatches = (data.batchesByExamSet[esId] || []).length > 0;
    setAddFor({ esId, mode: mode || (hasBatches ? 'existing' : 'new'), bid: bid || null });
  }, [data.batchesByExamSet]);
  const toggleSection = useCallback((key) => setCollapsed((prev) => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n;
  }), []);
  const toggleIvOpen = useCallback((esId) => setIvOpen((prev) => {
    const n = new Set(prev); if (n.has(esId)) n.delete(esId); else n.add(esId); return n;
  }), []);
  /** A batch's "Report": Candidates > Batch view with only that batch expanded. */
  const openBatchReport = useCallback((esId, bid) => {
    setIvSel(null);
    setCollapsed(new Set((data.batchesByExamSet[esId] || []).filter((b) => b.id !== bid).map((b) => `${esId}:${b.id}`)));
    setBatchIv(esId);
    setCandView('batch');
    setActiveTab('candidates');
  }, [data.batchesByExamSet, setActiveTab]);
  const openInterviewReport = useCallback((examSetId) => {
    setIvSel(null);
    setCandView('interview');
    setCandIv(examSetId);
    setActiveTab('candidates');
  }, [setActiveTab]);
  const openInterview = useCallback((examSetId) => { setIvSel(examSetId); setActiveTab('interviews'); }, [setActiveTab]);
  const openCandidate = useCallback((key) => { setCandOpen(key); setActiveTab('candidates'); }, [setActiveTab]);
  const openQuestionSet = useCallback((setId) => { setQset(setId); setActiveTab('questions'); }, [setActiveTab]);
  const openNewInterview = useCallback(() => { setIvReturn(null); setNewOpen(true); setActiveTab('interviews'); }, [setActiveTab]);
  const needQuestionsFor = useCallback((typeId, setId) => {
    setNewOpen(false);
    setIvReturn(typeId);
    openQuestionSet(setId);
  }, [openQuestionSet]);
  const returnToInterviewSetup = useCallback(() => {
    qbDirty.current = false; // just saved
    setNewOpen(true);
    setActiveTab('interviews');
  }, [setActiveTab]);

  if (loading) {
    return (
      <div className="wz wz-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p className="wz-sub">Loading position...</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="wz wz-page">
        <div className="wz-empty">
          <p>Position not found</p>
          <Button variant="primary" onClick={() => navigate('/admin/campaigns')}>Back to Positions</Button>
        </div>
      </div>
    );
  }

  return (
    <HireBarProvider value={data.hireBar}>
    <ToastProvider>
    <CampaignRulesProvider campaign={campaign}>
    <div className="wz wz-page">
      <PositionHeader
        campaign={campaign}
        hireBar={data.hireBar}
        counts={data.counts}
        activeTab={activeTab}
        onTab={setActiveTab}
        onBack={() => guardLeave(() => navigate('/admin/campaigns'))}
        onEdit={() => guardLeave(() => navigate(`/admin/campaigns/${id}/edit`))}
        onAccessChanged={() => data.reloadCampaign({ quiet: true })}
      />
      {pendingLeave && (
        <UnsavedChangesDialog
          onStay={() => setPendingLeave(null)}
          onLeave={() => { const go = pendingLeave; qbDirty.current = false; setPendingLeave(null); go(); }}
        />
      )}
      <main className="wz-view">
        {activeTab === 'overview' && (
          <OverviewTab
            campaign={campaign}
            people={data.people}
            examSets={data.examSets}
            hireBar={data.hireBar}
            questions={data.questions}
            codingQuestions={data.codingQuestions}
            onGoTab={setActiveTab}
            onOpenInterview={openInterview}
            onOpenCandidate={openCandidate}
            onOpenQuestionSet={openQuestionSet}
            onNewInterview={openNewInterview}
          />
        )}

        {activeTab === 'questions' && (
          <QuestionBankTab
            campaign={campaign}
            data={data}
            qset={qset}
            onQsetChange={setQset}
            onOpenInterview={openInterview}
            onDirtyChange={onQbDirtyChange}
            returnLabel={ivReturn ? CATALOG_TYPES.find((t) => t.id === ivReturn)?.name : null}
            onQuestionsSaved={ivReturn ? returnToInterviewSetup : undefined}
            onReturn={ivReturn ? () => guardLeave(returnToInterviewSetup) : undefined}
            onCancelReturn={() => setIvReturn(null)}
          />
        )}

        {activeTab === 'interviews' && (
          <InterviewsTab
            data={data}
            hireBar={data.hireBar}
            selectedId={ivSel}
            onOpenInterview={openInterview}
            onReport={openInterviewReport}
            onNewInterview={() => { setIvReturn(null); setNewOpen(true); }}
            batchesByExamSet={data.batchesByExamSet}
            openRows={ivOpen}
            onToggleRow={toggleIvOpen}
            onToggleAll={(open) => setIvOpen(open ? new Set(data.examSets.map((es) => es.id)) : new Set())}
            renderBatchRows={(es) => (
              <BatchRows
                campaign={campaign}
                examSet={es}
                data={data}
                hireBar={data.hireBar}
                onAdd={(esId, bid) => openAdd(esId, 'existing', bid)}
                onReport={openBatchReport}
                onNew={(esId) => openAdd(esId, 'new')}
              />
            )}
          />
        )}
        {activeTab === 'interviews' && ivSel && data.examSets.some((es) => es.id === ivSel) && (
          <InterviewPanel
            campaign={campaign}
            examSet={data.examSets.find((es) => es.id === ivSel)}
            data={data}
            hireBar={data.hireBar}
            onClose={() => setIvSel(null)}
            onAddCandidates={() => openAdd(ivSel)}
            onViewReport={() => openInterviewReport(ivSel)}
            onDeleted={() => { setIvSel(null); data.reloadExamSets(); data.reloadCandidates(); data.reloadBatches(); }}
            batches={data.batchesByExamSet[ivSel] || []}
            onNewBatch={() => openAdd(ivSel, 'new')}
            renderBatchesTab={(es) => (
              <BatchesTab
                examSet={es}
                data={data}
                hireBar={data.hireBar}
                onAdd={(esId, bid) => openAdd(esId, 'existing', bid)}
                onReport={openBatchReport}
                onNew={(esId) => openAdd(esId, 'new')}
              />
            )}
            groupByBatch={(es, entries) => groupEntriesByBatch(data, es, entries)}
          />
        )}
        {newOpen && (
          <NewInterviewDialog
            campaign={campaign}
            data={data}
            initialTypeId={ivReturn}
            onNeedQuestions={needQuestionsFor}
            onAddBatch={(esId) => {
              setNewOpen(false);
              setIvReturn(null);
              setIvOpen((prev) => new Set([...prev, esId]));
              openAdd(esId, 'new');
            }}
            onClose={() => { setNewOpen(false); setIvReturn(null); }}
            onCreated={async (examSet) => {
              setNewOpen(false);
              setIvReturn(null);
              await Promise.all([data.reloadExamSets(), data.reloadQuestions()]);
              // Step 3 "Add candidates": hand over to the Add candidates dialog.
              if (examSet?.id) setAddFor({ esId: examSet.id, mode: 'new', bid: null });
            }}
          />
        )}
        {addFor && data.examSets.some((es) => es.id === addFor.esId) && (
          <BatchAddCandidatesDialog
            key={`${addFor.esId}:${addFor.mode}:${addFor.bid}`}
            mode={addFor.mode}
            batchId={addFor.bid}
            campaign={campaign}
            examSet={data.examSets.find((es) => es.id === addFor.esId)}
            data={data}
            onClose={() => setAddFor(null)}
            onDone={(ctx) => { if (ctx?.created) setIvOpen((prev) => new Set([...prev, addFor.esId])); return data.reloadAssignments(); }}
          />
        )}

        {activeTab === 'candidates' && (
          <CandidatesTab
            campaign={campaign}
            data={data}
            hireBar={data.hireBar}
            view={candView}
            setView={setCandView}
            ivFilter={candIv}
            setIvFilter={setCandIv}
            openKey={candOpen}
            setOpenKey={setCandOpen}
            renderInterviewView={(ctx) => (
              <ByInterviewView
                data={data}
                hireBar={data.hireBar}
                ctx={ctx}
                collapsed={collapsed}
                onToggle={toggleSection}
                onSplitByBatch={(esId) => { setBatchIv(esId); setCandView('batch'); }}
              />
            )}
            renderBatchView={(ctx) => (
              <ByBatchView
                data={data}
                hireBar={data.hireBar}
                ctx={ctx}
                collapsed={collapsed}
                onToggle={toggleSection}
                batchIv={batchIv}
                setBatchIv={setBatchIv}
                onAdd={(esId, bid) => openAdd(esId, 'existing', bid)}
                onNewBatch={(esId) => openAdd(esId, 'new')}
              />
            )}
          />
        )}
      </main>
    </div>
    </CampaignRulesProvider>
    </ToastProvider>
    </HireBarProvider>
  );
};

export default CampaignDetail;
